/**
 * Search box ("/" focuses; spec §7). Queries /api/search after 2 chars and
 * shows the hits grouped by kind (families, addresses, txs, ledgers, hooks,
 * notes, views) with one keyboard cursor across the groups; Enter opens the
 * active hit's route, a raw address / tx hash opens directly.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useJobs, useSearch } from "@/api/hooks";
import type { SearchHit } from "@/api/types";
import { isAddress, isTxHash, routes } from "@/lib/jobs";
import { flattenGroups, groupHits, jobLabel, stepIndex } from "@/lib/search";
import { useShortcuts } from "@/lib/shortcuts";
import { useCurrentJob } from "@/state/job";

const KIND_GLYPH: Record<SearchHit["kind"], string> = {
  family: "👥",
  address: "◌",
  tx: "⇄",
  ledger: "▤",
  hook: "⚓",
  note: "✎",
  view: "▦",
};

/**
 * `compact`: fills its flex parent (phone top bar) instead of a fixed width.
 * `onDone`: the phone top bar opens the box on demand; it is told when the
 * reader leaves it (Escape) or follows a hit, to fold the box away again.
 */
export function SearchBox({ compact = false, onDone }: { compact?: boolean; onDone?: () => void }) {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const navigate = useNavigate();
  const job = useCurrentJob();
  const jobs = useJobs();
  const symbols = useMemo(() => {
    const m: Record<string, string> = {};
    for (const t of jobs.data?.data.tokens ?? []) m[t.id] = t.symbol;
    return m;
  }, [jobs.data]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 150);
    return () => clearTimeout(t);
  }, [q]);

  const search = useSearch({ q: debounced, limit: 40 });
  const groups = useMemo(() => groupHits(search.data?.data.hits ?? []), [search.data]);
  const flat = useMemo(() => flattenGroups(groups), [groups]);

  const direct = useMemo<SearchHit | null>(() => {
    const s = q.trim();
    if (job.kind !== "token" || !job.chain || !job.token) return null;
    if (isAddress(s))
      return {
        kind: "address",
        id: s.toLowerCase(),
        title: `open ${s.toLowerCase()} in ${symbols[job.id ?? ""] ?? "this job"}`,
        score: 1,
        route: routes.family(job.chain, job.token, s.toLowerCase()),
      };
    if (isTxHash(s))
      return {
        kind: "tx",
        id: s.toLowerCase(),
        title: `open tx ${s.slice(0, 10)}… in the tape`,
        score: 1,
        route: `${routes.tape(job.chain, job.token)}?tx=${s.toLowerCase()}`,
      };
    return null;
  }, [q, job.kind, job.chain, job.token, job.id, symbols]);

  const items = useMemo(() => (direct ? [direct, ...flat] : flat), [direct, flat]);
  const activeIdx = Math.min(active, Math.max(0, items.length - 1));

  useShortcuts([
    {
      key: "/",
      description: "focus search",
      group: "navigation",
      handler: () => {
        ref.current?.focus();
        ref.current?.select();
        setOpen(true);
      },
    },
  ]);

  // opened on demand (phone top bar): take the focus at once
  useEffect(() => {
    if (onDone) ref.current?.focus();
  }, [onDone]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  const go = (hit: SearchHit | undefined) => {
    if (!hit) return;
    void navigate(hit.route);
    setOpen(false);
    ref.current?.blur();
    onDone?.();
  };

  const showList = open && q.trim().length >= 2;
  const base = direct ? 1 : 0;

  return (
    // compact (the phone's top bar): no positioning here, so the list below hangs from the
    // whole bar (the header is relative) instead of from this narrower box
    <div className={compact ? "min-w-0 flex-1" : "relative shrink-0"}>
      <input
        ref={ref}
        className={`ctl ${compact ? "w-full min-w-0" : "w-32 @min-[1000px]:w-44 @min-[1200px]:w-60"}`}
        placeholder={compact ? "search  /" : "search address, name, tag, tx  /"}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            ref.current?.blur();
            onDone?.();
          } else if (e.key === "ArrowDown") {
            setActive(stepIndex(activeIdx, 1, items.length));
            e.preventDefault();
          } else if (e.key === "ArrowUp") {
            setActive(stepIndex(activeIdx, -1, items.length));
            e.preventDefault();
          } else if (e.key === "Enter") {
            go(items[activeIdx]);
          }
        }}
        aria-label="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls="search-results"
        aria-autocomplete="list"
      />
      {showList && (
        <ul
          id="search-results"
          ref={listRef}
          className={`absolute top-full z-30 mt-1 max-h-[70vh] overflow-auto rounded-sm border border-border bg-bg-elev py-1 shadow-lg ${compact ? "inset-x-2" : "right-0 w-[min(28rem,calc(100vw-1rem))]"}`}
          role="listbox"
        >
          {direct && (
            <HitRow
              hit={direct}
              idx={0}
              active={activeIdx === 0}
              onGo={go}
              onHover={setActive}
              symbols={symbols}
              currentJob={job.id}
            />
          )}
          {search.isLoading && <li className="px-2 py-1 text-fg-muted">searching…</li>}
          {search.isError && <li className="px-2 py-1 text-danger">search failed: {search.error.message}</li>}
          {!search.isLoading && !search.isError && flat.length === 0 && (
            <li className="px-2 py-1 text-fg-muted">no hits for “{debounced}”</li>
          )}
          {groups.map((g) => {
            const start = base + g.offset;
            return (
              <li key={g.kind} role="presentation">
                <div className="flex items-center gap-1 px-2 pt-1 pb-0.5 text-3xs font-semibold uppercase tracking-wider text-fg-faint">
                  <span aria-hidden>{KIND_GLYPH[g.kind]}</span>
                  {g.label} <span className="font-normal">({g.hits.length})</span>
                </div>
                <ul role="group" aria-label={g.label}>
                  {g.hits.map((h, i) => (
                    <HitRow
                      key={`${h.kind}:${h.id}`}
                      hit={h}
                      idx={start + i}
                      active={activeIdx === start + i}
                      onGo={go}
                      onHover={setActive}
                      symbols={symbols}
                      currentJob={job.id}
                    />
                  ))}
                </ul>
              </li>
            );
          })}
          <li className="mt-1 border-t border-border px-2 pt-1 text-2xs text-fg-faint" role="presentation">
            ↑↓ move · enter open · esc close
          </li>
        </ul>
      )}
    </div>
  );
}

function HitRow({
  hit,
  idx,
  active,
  onGo,
  onHover,
  symbols,
  currentJob,
}: {
  hit: SearchHit;
  idx: number;
  active: boolean;
  onGo: (h: SearchHit) => void;
  onHover: (i: number) => void;
  symbols: Record<string, string>;
  currentJob: string | undefined;
}) {
  const elsewhere = hit.job && hit.job !== currentJob ? jobLabel(hit.job, symbols) : undefined;
  return (
    <li
      role="option"
      aria-selected={active}
      data-idx={idx}
      className={`flex cursor-pointer items-baseline gap-2 px-2 py-0.5 ${active ? "bg-bg-hover" : ""}`}
      onMouseDown={(e) => {
        e.preventDefault();
        onGo(hit);
      }}
      onMouseEnter={() => onHover(idx)}
      title={hit.route}
    >
      <span className={`truncate ${hit.kind === "address" || hit.kind === "tx" ? "num" : ""}`}>
        {hit.title}
      </span>
      {hit.subtitle && <span className="min-w-0 flex-1 truncate text-2xs text-fg-muted">{hit.subtitle}</span>}
      {elsewhere && (
        <span className="ml-auto shrink-0 rounded-sm border border-border px-1 text-3xs text-fg-faint">
          {elsewhere}
        </span>
      )}
    </li>
  );
}
